/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, Button, Form, Spinner,
} from 'react-bootstrap';
import BillingoService, { makeIdempotencyKey } from '../../Services/BillingoService';
import PaymentsService from '../../Services/PaymentsService';
import { apiErrorText, formatAmount, unDeductedAdvances } from '../../Utils/billingoDocuments';

// Compact label for a recorded payment in the picker — mirrors the format used
// elsewhere (date · amount · channel).
function paymentLabel(p) {
  const date = p.occurred_at ? new Date(p.occurred_at).toISOString().slice(0, 10) : '';
  const amt = p.gross_amount != null ? formatAmount(p.gross_amount, p.currency) : '';
  const channel = p.external_refs?.stripe_charge_id
    ? 'card'
    : (p.external_refs?.bank_reference || p.external_refs?.wise_transfer_id || '');
  return [date, amt, channel].filter(Boolean).join(' · ') || (p._id || p.id);
}

// Payments recorded against any booking the document bills — the same set its
// payment-driven "Paid" status is derived from. A payment split across several
// of those bookings comes back once per booking; list it once.
async function loadPaymentsForBookings(bookingIds) {
  const lists = await Promise.all(
    [...new Set(bookingIds)].map((id) => PaymentsService.listForBooking(id).catch(() => [])),
  );
  const byId = new Map();
  lists.flat().forEach((p) => byId.set(String(p._id || p.id), p));
  return [...byId.values()];
}

// Create a final Számla from a Díjbekérő. The operator can mark the new Számla
// settled by a recorded payment, and the backend registers the portion that
// payment allocates to this document's bookings (so a payment bundled across
// several bookings only contributes its share). Self-contained so every entry
// point (booking panel, Invoicing list) offers the same payments.
function BillingoCreateSzamlaModal({
  doc = null, show = false, onHide, onCreated = () => {},
}) {
  const [payments, setPayments] = useState([]);
  const [siblingDocs, setSiblingDocs] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // One idempotency key per opening of the dialog, so a double-click issues
  // one Számla. It is renewed only when the API ANSWERED with a refusal: the
  // refused attempt keeps its key, and the API would answer a retry under it
  // with that failed attempt instead of trying again. A request that got no
  // answer keeps the key, so its retry finds a Számla that was issued after all.
  const attemptKey = useRef(null);

  const bookingKey = (doc?.booking_ids || []).join(',');
  useEffect(() => {
    setPayments([]);
    setSiblingDocs([]);
    setPaymentId('');
    setError('');
    attemptKey.current = null;
    if (!show || !bookingKey) return undefined;
    // Ignore a slow response for a Díjbekérő the operator has moved on from.
    let current = true;
    setLoadingPayments(true);
    const ids = bookingKey.split(',');
    // The booking's other documents are FETCHED rather than taken from the
    // caller on purpose: this modal opens from the per-order panel AND from
    // the paged "All invoices" list, where a sibling advance can easily be on
    // another page. A warning that depends on which page you were looking at
    // is not a warning.
    loadPaymentsForBookings(ids)
      .then((list) => { if (current) setPayments(list); })
      .finally(() => { if (current) setLoadingPayments(false); });
    // Promise.resolve(): the warning is best-effort, so a service that
    // answers with nothing usable must not stop the dialog opening — the same
    // reason the account prefill in BillingoDocumentFlow wraps its search.
    Promise.resolve(BillingoService.listDocumentsForBookingIds(ids))
      .then((list) => { if (current) setSiblingDocs(list || []); })
      .catch(() => {});
    return () => { current = false; };
  }, [show, doc?.billingo_document_id, bookingKey]);

  // Only an inbound, completed payment can settle an invoice.
  const settling = payments.filter((p) => p.direction === 'in' && p.status === 'completed');
  // Advances this conversion cannot net off — Billingo's create-from-proforma
  // takes no `advance_invoice`. See unDeductedAdvances for why this warns
  // rather than blocks.
  const advances = unDeductedAdvances(doc, siblingDocs);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    attemptKey.current = attemptKey.current || makeIdempotencyKey();
    try {
      const created = await BillingoService.createFromProforma({
        billingo_document_id: doc.billingo_document_id,
        payment_id: paymentId || null,
        idempotency_key: attemptKey.current,
      });
      if (created?.status === 'failed') {
        attemptKey.current = null;
        setError('Billingo refused this Számla — its reason is under Invoicing → Failed documents.');
        return;
      }
      onHide();
      onCreated();
    } catch (err) {
      if (err.response) attemptKey.current = null;
      setError(apiErrorText(err, 'Failed to create the Számla'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Számla from Díjbekérő</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {doc && (
          <p className="mb-3">
            Issue a final
            {' '}
            <strong>Számla</strong>
            {' from '}
            <strong>{doc.number}</strong>
            {doc.total != null ? ` · ${formatAmount(doc.total, doc.currency)}` : ''}
            .
          </p>
        )}
        {loadingPayments && (
          <div className="text-muted small">
            <Spinner size="sm" animation="border" className="me-2" />
            Loading recorded payments…
          </div>
        )}
        {!loadingPayments && settling.length > 0 && (
          <Form.Group>
            <Form.Label className="small text-muted">Settled by payment (optional)</Form.Label>
            <Form.Select value={paymentId} onChange={(e) => setPaymentId(e.target.value)}>
              <option value="">Not paid yet</option>
              {settling.map((p) => {
                const pid = p._id || p.id;
                return (
                  <option key={pid} value={pid}>
                    {paymentLabel(p)}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Text className="text-muted">
              Marks the Számla paid in Billingo for the amount this payment
              allocates to the booking.
            </Form.Text>
          </Form.Group>
        )}
        {!loadingPayments && settling.length === 0 && (
          <div className="text-muted small">
            No recorded inbound payment to link — the Számla will be issued unpaid.
          </div>
        )}
        {advances.length > 0 && doc && (
          <div className="alert alert-warning py-2 mt-3 mb-0" data-testid="proforma-advance-warning">
            <div className="fw-semibold small">
              <i className="fa fa-triangle-exclamation me-1" />
              {advances.length === 1
                ? 'An Előlegszámla is live on this booking'
                : `${advances.length} Előlegszámlas are live on this booking`}
            </div>
            <ul className="mb-1 mt-1 ps-3 small">
              {advances.map((advance) => (
                <li key={advance.id || advance.number}>
                  {`${advance.number} · ${formatAmount(advance.total, advance.currency)}`}
                </li>
              ))}
            </ul>
            <div className="small mb-0">
              {'Converting a Díjbekérő cannot deduct an advance, so this Számla is issued for '}
              <strong>{formatAmount(doc.total, doc.currency)}</strong>
              {' and the trip ends up invoiced for '}
              <strong>
                {formatAmount(
                  (Number(doc.total) || 0)
                    + advances.reduce((sum, a) => sum + (Number(a.total) || 0), 0),
                  doc.currency,
                )}
              </strong>
              {' in total. Right if this Díjbekérő is the balance after the advance; wrong if'}
              {' it is the full amount — then use "Create document", which deducts the advance.'}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={submitting || loadingPayments}>
          {submitting ? 'Creating…' : 'Create Számla'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BillingoCreateSzamlaModal;
